"use client"

import { useState, useRef, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  Paperclip,
  FileText,
  ImageIcon,
  Download,
  Eye,
  Trash2,
  X,
  File,
  Video,
  Music,
  Archive,
  Upload,
  RotateCw,
} from "lucide-react"
import toast from "react-hot-toast"

const ACCEPTED_TYPES = {
  "image/*": ["jpg", "jpeg", "png", "gif", "webp", "svg"],
  "application/pdf": ["pdf"],
  "application/msword": ["doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "text/plain": ["txt"],
  "application/zip": ["zip"],
  "application/x-rar-compressed": ["rar"],
  "video/*": ["mp4", "avi", "mov", "wmv"],
  "audio/*": ["mp3", "wav", "ogg"],
}

export default function InlineAttachmentManager({
  files = [],
  onFilesChange,
  maxFiles = 5,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = Object.keys(ACCEPTED_TYPES),
  allowMultiple = true,
  showPreview = true,
  uploadEndpoint = "/api/upload",
  downloadEndpoint = "/api/download",
  className = "",
  disabled = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState(new Set())

  const fileInputRef = useRef(null)

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) return ImageIcon
    if (fileType.startsWith("video/")) return Video
    if (fileType.startsWith("audio/")) return Music
    if (fileType.includes("pdf")) return FileText
    if (fileType.includes("zip") || fileType.includes("rar")) return Archive
    return File
  }

  const getFileCategory = (fileType) => {
    if (fileType.startsWith("image/")) return "image"
    if (fileType.startsWith("video/")) return "video"
    if (fileType.startsWith("audio/")) return "audio"
    if (fileType.includes("zip") || fileType.includes("rar")) return "archive"
    if (fileType.includes("pdf") || fileType.includes("doc") || fileType.includes("text")) return "document"
    return "other"
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const validateFile = (file) => {
    if (file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)}`
    }

    const isValidType = acceptedTypes.some((type) => {
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.slice(0, -1))
      }
      return file.type === type
    })

    if (!isValidType) {
      return "File type not supported"
    }

    if (files.length >= maxFiles) {
      return `Maximum ${maxFiles} files allowed`
    }

    return null
  }

  const generateFileId = () => {
    return Math.random().toString(36).substr(2, 9)
  }

  const uploadFile = async (file) => {
    const fileId = generateFileId()

    // Create initial file object
    const attachmentFile = {
      id: fileId,
      name: file.name,
      originalName: file.name,
      size: file.size,
      type: file.type,
      uploadProgress: 0,
      status: "uploading",
      uploadedAt: new Date(),
      uploadedBy: {
        id: "current-user",
        name: "Current User",
      },
      category: getFileCategory(file.type),
      tags: [],
      description: "",
    }

    // Add to uploading files
    setUploadingFiles((prev) => new Set(prev).add(fileId))

    try {
      // Convert file to base64
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
        })
      }

      // Simulate upload progress while converting to base64
      const uploadPromise = new Promise(async (resolve, reject) => {
        try {
          let progress = 0
          const interval = setInterval(() => {
            progress += Math.random() * 20
            if (progress >= 90) {
              clearInterval(interval)
            } else {
              // Update progress
              onFilesChange([...files.filter((f) => f.id !== fileId), { ...attachmentFile, uploadProgress: progress }])
            }
          }, 100)

          // Convert to base64
          const base64Data = await convertToBase64(file)

          clearInterval(interval)

          // Complete the upload
          setTimeout(() => {
            resolve({
              ...attachmentFile,
              status: "completed",
              uploadProgress: 100,
              base64: base64Data, // Store base64 data
              url: base64Data, // For preview purposes
              thumbnail: file.type.startsWith("image/") ? base64Data : undefined,
            })
          }, 300)
        } catch (error) {
          reject(error)
        }
      })

      const result = await uploadPromise
      setUploadingFiles((prev) => {
        const newSet = new Set(prev)
        newSet.delete(fileId)
        return newSet
      })

      return result
    } catch (error) {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev)
        newSet.delete(fileId)
        return newSet
      })

      throw error
    }
  }

  const handleFileSelect = async (selectedFiles) => {
    const fileArray = Array.from(selectedFiles)

    for (const file of fileArray) {
      const error = validateFile(file)
      if (error) {
        toast.error(`${file.name}: ${error}`)
        continue
      }

      try {
        const uploadedFile = await uploadFile(file)
        onFilesChange([...files, uploadedFile])
        toast.success(`${file.name} uploaded successfully`)
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`)
      }
    }
  }

  const removeFile = (fileId) => {
    onFilesChange(files.filter((file) => file.id !== fileId))
    toast.success("File removed")
  }

  const downloadFile = async (file) => {
    try {
      if (file.base64) {
        // Convert base64 to blob for download
        const base64Data = file.base64.split(",")[1] // Remove data:type;base64, prefix
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: file.type })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = file.originalName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        toast.success("Download started")
      }
    } catch (error) {
      toast.error("Failed to download file")
    }
  }

  // Progress Bar Component
  const ProgressBar = ({ value, className = "" }) => (
    <div className={`w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-1.5 ${className}`}>
      <div
        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )

  // Button Component
  const Button = ({ children, variant = "default", size = "default", className = "", disabled = false, ...props }) => {
    const baseClasses =
      "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"

    const variants = {
      default: "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800",
      ghost: "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800",
      destructive: "bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800",
      outline:
        "border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800",
    }

    const sizes = {
      default: "px-4 py-2 text-sm rounded-lg",
      sm: "px-3 py-1.5 text-xs rounded-md",
      lg: "px-6 py-3 text-base rounded-lg",
    }

    return (
      <button
        className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    )
  }

  // File Preview Modal
  const FilePreview = ({ file }) => {
    const IconComponent = getFileIcon(file.type)

    return (
      <Transition appear show={isPreviewOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setIsPreviewOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl transition-all">
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <Dialog.Title className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                        <IconComponent className="h-5 w-5" />
                        {file.originalName}
                      </Dialog.Title>
                      <button
                        onClick={() => setIsPreviewOpen(false)}
                        className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* File Preview */}
                    <div className="mb-6">
                      {file.type.startsWith("image/") && file.url ? (
                        <img
                          src={file.url || "/placeholder.svg"}
                          alt={file.name}
                          className="w-full max-h-64 object-contain rounded-lg border border-neutral-200 dark:bg-neutral-800"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg">
                          <IconComponent className="h-12 w-12 text-neutral-400 mb-2" />
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">Preview not available</p>
                        </div>
                      )}
                    </div>

                    {/* File Details */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">File Size:</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">File Type:</span>
                        <span className="font-medium text-neutral-900 dark:text-white">{file.type}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">Uploaded:</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {file.uploadedAt.toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button onClick={() => downloadFile(file)} className="flex-1">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          removeFile(file.id)
                          setIsPreviewOpen(false)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    )
  }

  // Attachment Modal
  const AttachmentModal = () => (
    <Transition appear show={isModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setIsModalOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl transition-all">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title className="text-lg font-bold text-neutral-900 dark:text-white">
                      Manage Attachments
                    </Dialog.Title>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Upload Area */}
                  <div className="mb-6">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple={allowMultiple}
                      accept={acceptedTypes.join(",")}
                      onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                      className="hidden"
                    />

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-4 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-neutral-400" />
                        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Choose Files</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Max {maxFiles} files, up to {formatFileSize(maxFileSize)} each
                        </p>
                      </div>
                    </button>
                  </div>

                  {/* File List */}
                  {files.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {files.map((file) => {
                        const IconComponent = getFileIcon(file.type)
                        return (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 p-3 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg"
                          >
                            <div className="flex-shrink-0">
                              {file.thumbnail ? (
                                <img
                                  src={file.thumbnail || "/placeholder.svg"}
                                  alt={file.name}
                                  className="w-8 h-8 object-cover rounded border border-neutral-200 dark:border-neutral-600"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-neutral-200 dark:bg-neutral-700 rounded flex items-center justify-center">
                                  <IconComponent className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                {formatFileSize(file.size)}
                              </p>

                              {file.status === "uploading" && (
                                <div className="mt-1">
                                  <ProgressBar value={file.uploadProgress || 0} />
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              {showPreview && file.status === "completed" && (
                                <button
                                  onClick={() => {
                                    setSelectedFile(file)
                                    setIsPreviewOpen(true)
                                  }}
                                  className="p-1.5 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              )}
                              {file.status === "completed" && (
                                <button
                                  onClick={() => downloadFile(file)}
                                  className="p-1.5 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => removeFile(file.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )

  const hasUploadingFiles = files.some((f) => f.status === "uploading")
  const completedFilesCount = files.filter((f) => f.status === "completed").length

  return (
    <div className={`relative ${className}`}>
      {/* Attachment Button */}
      <button
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        className={`
          relative w-11 h-11 flex justify-center items-center rounded-lg transition-colors
          ${disabled
            ? "text-neutral-400 cursor-not-allowed"
            : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
          }
        `}
        title="Attach files"
      >
        <Paperclip className="h-5 w-5" />

        {/* File Count Badge */}
        {completedFilesCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {completedFilesCount}
          </span>
        )}

        {/* Upload Progress Indicator */}
        {hasUploadingFiles && (
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
            <RotateCw className="h-3 w-3 text-blue-600 animate-spin" />
          </div>
        )}
      </button>

      {/* Attachment Modal */}
      <AttachmentModal />

      {/* File Preview Modal */}
      {selectedFile && <FilePreview file={selectedFile} />}
    </div>
  )
}
